import { Box } from '@chakra-ui/react';
import { useMemo } from 'react';
import DownloadApp from 'components/ThankYouView/DownloadApp';
import { ThankYouStepRow } from 'components/ThankYouView/ThankYouStepRow';
import { FormCard, Heading, Text } from 'components/ui';
import { Meeting } from '../../models/meeting';

type Props = {
  bookedMeeting?: Meeting;
  timezone?: string;
  email?: string;
};

const ThankYouAppointmentBooked = ({
  bookedMeeting,
  timezone,
  email,
}: Props) => {
  const meetingDateFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat('en-US', {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
        timeZone: timezone,
        timeZoneName: 'short',
      }),
    [timezone]
  );

  const bookedMeetingSummary = bookedMeeting
    ? `${meetingDateFormatter.format(
        new Date(bookedMeeting.meeting_date)
      )} with ${bookedMeeting.provider?.display_name}`
    : undefined;

  return (
    <>
      <Text
        fontFamily={'body'}
        fontSize={'16.5px'}
        fontWeight={400}
        lineHeight={'27.225px'}
        color={'blue.500'}
        textAlign={'center'}
      >
        From here on out, everything you need happens right on your phone: your
        personalized plan, messaging with your care team, video visits, and
        more.
      </Text>

      <FormCard
        borderColor={'blue.100'}
        data-testid={'thank-you-appointment-booked'}
      >
        <Heading textAlign={'left'} fontSize={'22px'} lineHeight={'33px'}>
          What to do next
        </Heading>

        <ThankYouStepRow
          complete
          title={'Visit booked'}
          body={bookedMeetingSummary}
        />
        <Box bg={'blue.100'} py={4} px={3} borderRadius={14}>
          <ThankYouStepRow
            step={1}
            title={'Get the app on your phone'}
            children={<DownloadApp />}
          />
        </Box>
        <ThankYouStepRow
          step={2}
          title={
            email
              ? 'Log in with the email you just used'
              : 'Log in with the email you used to register'
          }
          children={
            email && (
              <Box
                bg={'#E4EEF1'}
                rounded={8}
                px={'12px'}
                py={'6px'}
                alignSelf={'flex-start'}
              >
                <Text
                  color={'blueGray.600'}
                  fontFamily={'body'}
                  fontSize={'13.5px'}
                  fontWeight={700}
                >
                  {email}
                </Text>
              </Box>
            )
          }
        />
        <ThankYouStepRow
          step={3}
          title={'Start setting up your Care Guide'}
          body={
            'This takes about 5 minutes and helps your care team personalize your first appointment.'
          }
        />
        <ThankYouStepRow
          step={4}
          title={'Message your care team anytime'}
          body={'Have a question before your visit? Send it right in the app.'}
        />
      </FormCard>
    </>
  );
};

export default ThankYouAppointmentBooked;
